export const FILE_PARENT_TYPES = [
  'chat_messages',
  'menus',
  'recipes',
  'menu_imports',
  'ingredients',
  'image_generations'
] as const;

export type FileParentType = (typeof FILE_PARENT_TYPES)[number];

export const IMAGE_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp'
] as const;

export const PDF_CONTENT_TYPE = 'application/pdf' as const;

export const DOCX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document' as const;

export const ALLOWED_FILE_CONTENT_TYPES = [
  ...IMAGE_CONTENT_TYPES,
  PDF_CONTENT_TYPE,
  DOCX_CONTENT_TYPE
] as const;

export type AllowedFileContentType =
  (typeof ALLOWED_FILE_CONTENT_TYPES)[number];

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_PDF_BYTES = 20 * 1024 * 1024;
export const MAX_DOCX_BYTES = 10 * 1024 * 1024;
export const MAX_FILES_PER_PARENT = 10;

export const FILE_ACCEPT: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
  [DOCX_CONTENT_TYPE]: ['.docx']
};

export const MENU_IMPORT_ACCEPT: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
  [DOCX_CONTENT_TYPE]: ['.docx']
};

export const ZIP_CONTENT_TYPE = 'application/zip' as const;

/**
 * iPhone captures. Never stored or sent to the model as-is: the browser
 * converts them to JPEG before upload and the server converts Drive
 * downloads, so `ALLOWED_FILE_CONTENT_TYPES` does not include them.
 */
export const HEIC_CONTENT_TYPES = ['image/heic', 'image/heif'] as const;

export const HEIC_ACCEPT: Record<string, string[]> = {
  'image/heic': ['.heic'],
  'image/heif': ['.heif']
};

/** Files a menu-import batch entry may be before browser-side conversion. */
export const MENU_IMPORT_ENTRY_ACCEPT: Record<string, string[]> = {
  ...MENU_IMPORT_ACCEPT,
  ...HEIC_ACCEPT
};

/**
 * What the menu-import file picker lets the user choose. A `.zip` is expanded
 * in the browser into its entries and HEIC is converted to JPEG there too;
 * neither reaches the server in its original form.
 */
export const MENU_IMPORT_PICKER_ACCEPT: Record<string, string[]> = {
  ...MENU_IMPORT_ENTRY_ACCEPT,
  [ZIP_CONTENT_TYPE]: ['.zip']
};

export function isHeicContentType(contentType: string): boolean {
  return (HEIC_CONTENT_TYPES as readonly string[]).includes(contentType);
}

/** Largest zip archive the browser will expand for menu import. */
export const MAX_ZIP_BYTES = 200 * 1024 * 1024;

/** Most files a single menu-import batch (zip, Drive, Photos) may contain. */
export const MAX_MENU_IMPORT_BATCH = 25;

export function isImageContentType(contentType: string): boolean {
  return (IMAGE_CONTENT_TYPES as readonly string[]).includes(contentType);
}

export function isPdfContentType(contentType: string): boolean {
  return contentType === PDF_CONTENT_TYPE;
}

export function isDocxContentType(contentType: string): boolean {
  return contentType === DOCX_CONTENT_TYPE;
}

export function isAllowedFileContentType(
  contentType: string
): contentType is AllowedFileContentType {
  return (ALLOWED_FILE_CONTENT_TYPES as readonly string[]).includes(
    contentType
  );
}

export function maxBytesForContentType(contentType: string): number {
  if (isPdfContentType(contentType)) {
    return MAX_PDF_BYTES;
  }
  if (isDocxContentType(contentType)) {
    return MAX_DOCX_BYTES;
  }
  return MAX_IMAGE_BYTES;
}

export function isFileParentType(value: string): value is FileParentType {
  return (FILE_PARENT_TYPES as readonly string[]).includes(value);
}
